import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { Newsletter } from '@df/session/newsletter/newsletter.interfaces';
import { NewsletterService } from '@df/session/newsletter/newsletter.service';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import each from 'lodash-es/each';
import { Subject, Subscription } from 'rxjs';
import { CheckboxComponent } from '../ui/form/checkbox';
import { InputWrapBodyDirective } from '../ui/form/input-wrap-body.directive';
import { InputWrapComponent } from '../ui/form/input-wrap.component';
declare module '@df/session/newsletter/newsletter.interfaces' {
    namespace Newsletter {
        interface ISubscribeRequest {
            type?: string[];
        }
        interface IUpdateRequest {
            newsletter?: boolean;
        }
    }
}
export interface INewsletterCheckboxesData {
    post_optin?: boolean;
    sms_optin?: boolean;
}
type INewsletterCheckboxesBlockData = Catalog.IBlockData<INewsletterCheckboxesBlockDataContent>;
interface INewsletterCheckboxesBlockDataContent {
    title: string;
    subtitle?: string;
    email_checkbox_copy: string;
    sms_checkbox_copy: string;
    post_checkbox_copy: string;
    copy: string;
}
@Component({
    selector: 'newsletter-checkboxes',
    templateUrl: './newsletter-checkboxes.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CheckboxComponent, FormsModule, InputWrapComponent, CommonModule, SizeClassDirective, ICmsClientModule, InputWrapBodyDirective]
})
@ICmsComponent<NewsletterCheckboxesComponent>({
    id: 'newsletter-checkboxes',
    block: {
        url: 'newsletter-checkboxes'
    },
    onDemandOnly: true,
    name: 'Newsletter Checkboxes',
    fields: [
        { key: 'title', type: NICmsContentField.EFieldType.text, title: 'Title', required: true },
        { key: 'subtitle', type: NICmsContentField.EFieldType.text, title: 'Subtitle' },
        { key: 'email_checkbox_copy', type: NICmsContentField.EFieldType.text, title: 'Email checkbox copy', required: true },
        { key: 'sms_checkbox_copy', type: NICmsContentField.EFieldType.text, title: 'SMS checkbox copy', required: true },
        { key: 'post_checkbox_copy', type: NICmsContentField.EFieldType.text, title: 'Post checkbox copy', required: true },
        { key: 'copy', type: NICmsContentField.EFieldType.text, title: 'Copy', required: true }
    ]
})
export class NewsletterCheckboxesComponent
    extends UiComponent
    implements NICmsContentComponent.IComponent, OnChanges, OnDestroy, OnInit
{
    @ICmsContent({
        type: 'blockContent'
    })
    content?: INewsletterCheckboxesBlockDataContent;
    @Input()
    email?: string;
    @Input()
    source?: string;
    @Input()
    variant?: string;
    @Input()
    checkoutData?: Checkout.IQuoteData;
    @Input()
    beforeSubmitSubject?: Subject<void>;
    @Input()
    resetSubject?: Subject<void>;
    @Input()
    showBrochureCheckbox? = false;
    @Input()
    checkboxesMessage? = false;

    @Output() optionChoosed = new EventEmitter<boolean>();

    data: INewsletterCheckboxesData = { post_optin: false, sms_optin: false };
    newsletter = false;
    private newsletterService = inject(NewsletterService);
    private beforeSubmitEventSubscription?: Subscription;
    private resetSubscription?: Subscription;
    override ngOnInit(): void {
        this.onChange();
        super.ngOnInit();
    }
    override ngOnChanges(changes): void {
        super.ngOnChanges(changes);
        if (changes.beforeSubmitSubject && !this.revertLogic) {
            this.unsubscribeBeforeSubmit();
            if (this.beforeSubmitSubject) {
                this.beforeSubmitEventSubscription = this.beforeSubmitSubject.subscribe(() => this.submit());
            }
        }
        if (changes.resetSubject) {
            this.unsubscribeReset();
            if (this.resetSubject) {
                this.resetSubscription = this.resetSubject.subscribe(() => {
                    each(this.data, (value, key) => {
                        this.data[key] = false;
                    });
                    this.detectChanges();
                });
            }
        }
    }
    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.unsubscribeBeforeSubmit();
        this.unsubscribeReset();
    }
    private unsubscribeBeforeSubmit(): void {
        this.beforeSubmitEventSubscription?.unsubscribe();
        this.beforeSubmitEventSubscription = undefined;
    }
    private unsubscribeReset(): void {
        this.resetSubscription?.unsubscribe();
        this.resetSubscription = undefined;
    }
    onChange(): void {
        if (this.checkoutData) {
            this.checkoutData.newsletter = this.newsletter;
            this.checkoutData.subscriberProperties = { sms_optin: !!this.data.sms_optin };
        }

        this.data.post_optin || this.data.sms_optin || this.newsletter ? this.optionChoosed.emit(true) : this.optionChoosed.emit(false);
        this.detectChanges();
    }
    submit(): Promise<any> | void {
        if (this.email) {
            const submitData = { ...this.data };
            Object.entries(submitData).forEach(([key, value]) => {
                if (!value) {
                    delete submitData[key];
                }
            });
            if (Object.keys(submitData).length || this.newsletter) {
                let data: Newsletter.IUpdateRequest = {
                    email: this.email,
                    source: this.source,
                    subscriberProperties: submitData
                };
                if (this.newsletter) {
                    data = { ...data, newsletter: this.newsletter };
                }
                return this.newsletterService.update(undefined, data);
            }
        }
    }
    get revertLogic() {
        return this.source === 'checkout';
    }
}
